// Client workspace (DESIGN §7 rule 2): /clients/:clientId[/months/:n][?view=months]
// client header (name, status, owner, start, contact; Edit) → month selector (01…NN, batch tags, Off months red)
// → the selected month's line (dates · batch · Mark month off / Resume · notes) → its batch(es) → Add Batch.
// "All months" swaps the month view for the compact timeline (Month | Dates | Batch | Progress | Status).
import './client.css';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { dispatch, getSnapshot, reload, useStore } from '../store.js';
import { isNotesConflict, sendOnUnload } from '../components/notes-sync.js';
import { mergeNotes } from '../components/notes-merge.js';
import { MAX_MONTH, clientHref, clientsHref, navigate } from '../router.js';
import { Page } from '../components/page.jsx';
import { BatchTable } from '../components/batch-row.jsx';
import { MonthSelector } from '../components/month-selector.jsx';
import { StageProgress } from '../components/stage-progress.jsx';
import { openAddBatch, openEditClient } from '../components/forms.jsx';
import { Avatar, Button, EmptyState, ExternalLink, StatusBadge, cx, toast } from '../components/ui.jsx';
import { Icon } from '../icons.jsx';
import { batchLabel, monthLabel, pad2 } from '../../shared/constants.js';
import { clientSummary, monthStatus, stageProgressText } from '../../shared/derive.js';
import { formatDate, formatDateRange, monthNumberForDate, monthRange } from '../../shared/dates.js';
import { clientContract, instagramUrl, picOf, postingDaysOf, postingDaysText, tiktokUrl } from '../contract.js';

// ---------- model (guarded: works with and without the v3 shared layer) ----------

export const isOffMonth = (month) => month?.off === true;
const byNumber = (a, b) => a.batchNumber - b.batchNumber;

// The date-based month the client is in today (1–99), or 0 before the start date.
export function nowMonthOf(client, today) {
  if (!client?.startDate || !today || today < client.startDate) return 0;
  return monthNumberForDate(client.startDate, today, MAX_MONTH);
}

// "B6" | "B6+" (more than one batch in the month) | ''.
export function batchTag(batches) {
  if (!batches?.length) return '';
  return `B${batches[0].batchNumber}${batches.length > 1 ? '+' : ''}`;
}

// "Batch 06" | "Batch 06, 07" | ''.
export function batchNames(batches) {
  if (!batches?.length) return '';
  return `${batchLabel(batches[0].batchNumber)}${batches.slice(1).map((b) => `, ${pad2(b.batchNumber)}`).join('')}`;
}

// Every month of a client, 1…NN: NN = max(12, the date-based current month, the highest month holding a batch or an
// Off flag), capped at 99 (v3 `summary.monthCount` wins when present). Months without a record (not generated yet)
// get their dates from the start date. → { count, nowMonth, months: [{ monthNumber, month|null, startDate, endDate,
// status, off, batches }], byNumber: Map }. atLeast: a month the URL asks for (extends the strip up to it).
export function clientMonths(state, index, client, today, atLeast = 0) {
  const summary = clientSummary(state, index, client.id, today);
  const rows = new Map((summary.months || []).map((r) => [r.monthNumber, r]));
  const nowMonth = nowMonthOf(client, today);
  let highest = 0;
  for (const r of rows.values()) {
    const list = index.batchesByMonth.get(r.month.id) || [];
    if (list.length || isOffMonth(r.month) || r.off === true) highest = Math.max(highest, r.monthNumber);
  }
  const count = Math.min(MAX_MONTH, Math.max(12, nowMonth, highest, Number(summary.monthCount) || 0, Number(atLeast) || 0));
  const months = [];
  for (let n = 1; n <= count; n++) {
    const r = rows.get(n);
    const dates = r?.month?.startDate ? { startDate: r.month.startDate, endDate: r.month.endDate } : monthRange(client.startDate, n);
    // Not stored yet → a virtual month (id ''); actions address it by { clientId, monthNumber } and create it.
    const month = r?.month || { id: '', clientId: client.id, monthNumber: n, ...dates, notes: '', off: false, virtual: true };
    const batches = month.id ? [...(index.batchesByMonth.get(month.id) || [])].sort(byNumber) : [];
    const off = isOffMonth(month) || r?.off === true;
    const status = off ? 'Off' : r?.status || monthStatus({ ...dates }, batches, today);
    months.push({ monthNumber: n, month, ...dates, status, off, batches });
  }
  return { count, nowMonth, months, byNumber: new Map(months.map((m) => [m.monthNumber, m])), summary };
}

// The client's "current batch": the lowest-numbered open batch, else the latest one (null without batches).
export function currentBatchOf(index, clientId) {
  const list = [...(index.batchesByClient.get(clientId) || [])].sort(byNumber);
  return list.find((b) => b.status !== 'Done') || list[list.length - 1] || null;
}

export function goToMonth(clientId, n, view) {
  navigate(clientHref(clientId, n, { view }).slice(1), { replace: true });
}


// ---------- auto-saving month notes ----------

// Debounced auto-save for a text field (DESIGN §3): saves `delay` ms after typing stops, on blur, on unmount, and when
// the tab is hidden or closed (`onUnload(text, base)`). save(text, base) → Promise of the text actually stored (a merge
// with someone else's newer text is shown). `base` = the stored text the edit started from (DATA-03: the server refuses
// a save whose base is out of date). External changes are followed while not typing.
// → { value, status: ''|'saving'|'saved'|'error', bind: { value, onInput, onFocus, onBlur } }
export function useAutosave(stored, save, { delay = 600, onUnload } = {}) {
  const [value, setValue] = useState(stored || '');
  const [status, setStatus] = useState('');
  const dirty = useRef(null);
  const focused = useRef(false);
  const timer = useRef(0);
  const fns = useRef({});
  fns.current = { save, onUnload };
  const base = useRef((stored || '').trim());

  useEffect(() => {
    if (focused.current || dirty.current != null) return;
    setValue(stored || '');
    base.current = (stored || '').trim();
  }, [stored]);

  const flush = () => {
    clearTimeout(timer.current);
    timer.current = 0;
    const text = dirty.current;
    if (text == null) return;
    dirty.current = null;
    setStatus('saving');
    fns.current.save(text, base.current).then((saved) => {
      if (typeof saved === 'string') base.current = saved.trim();
      if (dirty.current != null) return;
      if (typeof saved === 'string' && saved.trim() !== text.trim()) setValue(saved);
      setStatus('saved');
    }, () => setStatus('error'));
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;

  useEffect(() => {
    const leave = () => {
      if (dirty.current == null) return;
      clearTimeout(timer.current);
      const text = dirty.current;
      dirty.current = null;
      if (fns.current.onUnload) fns.current.onUnload(text, base.current);
      else fns.current.save(text, base.current).catch(() => {});
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') leave(); };
    window.addEventListener('pagehide', leave);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', leave);
      document.removeEventListener('visibilitychange', onVisibility);
      flushRef.current();
    };
  }, []);

  useEffect(() => {
    if (status !== 'saved') return undefined;
    const t = setTimeout(() => setStatus(''), 2000);
    return () => clearTimeout(t);
  }, [status]);

  const bind = {
    value,
    onInput: (e) => {
      const text = e.currentTarget.value;
      setValue(text);
      dirty.current = text;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => flushRef.current(), delay);
    },
    onFocus: () => { focused.current = true; },
    onBlur: () => { focused.current = false; flushRef.current(); },
  };
  return { value, status, bind, flush: () => flushRef.current() };
}

// { id } for a stored month, { clientId, monthNumber } for a virtual one (the action creates it).
export const monthRef = (month) => (month.id ? { id: month.id } : { clientId: month.clientId, monthNumber: month.monthNumber });

const storedMonthNotes = (month) => {
  const index = getSnapshot().index;
  const m = month.id ? index?.monthsById.get(month.id)
    : (index?.monthsByClient.get(month.clientId) || []).find((x) => x.monthNumber === month.monthNumber);
  return (m?.notes ?? '').trim();
};

// Sends `notesBase`; when someone else saved in between, re-reads, keeps both texts and saves the merge (DATA-03).
// → the text now stored.
async function saveMonthNotes(month, text, base) {
  const next = text.trim();
  if (storedMonthNotes(month) === next) return next;
  const send = (notes, notesBase) => dispatch('month.update', { ...monthRef(month), patch: { notes, notesBase } }, { toast: false });
  try {
    await send(next, base);
    return next;
  } catch (err) {
    if (!isNotesConflict(err)) {
      if (getSnapshot().status === 'ready') toast(err?.message || 'Notes not saved.', { tone: 'danger' });
      throw err;
    }
  }
  await Promise.resolve(reload()).catch(() => {});
  const fresh = storedMonthNotes(month);
  const merged = mergeNotes(base, next, fresh).trim();
  await send(merged, fresh);
  toast('Someone else edited these notes. Both changes were kept.', { duration: 5000 });
  return merged;
}


// ---------- view ----------

export function ClientView({ route }) {
  const { state, index, today } = useStore();
  const client = index.clientsById.get(route.params.clientId);
  const asked = route.params.monthNumber || 0;
  // The strip shows at least the contract (contractMonths + off months, DESIGN §9).
  const contractEnd = client ? clientContract(state, index, client, today)?.endMonth || 0 : 0;
  const model = useMemo(() => (client ? clientMonths(state, index, client, today, Math.max(asked, contractEnd)) : null), [state, index, client, today, asked, contractEnd]);

  if (!client) {
    return (
      <Page title="Client not found">
        <EmptyState
          title="This client does not exist or was removed."
          action={<Button href={clientsHref()}>Back to Clients</Button>}
        />
      </Page>
    );
  }

  const timeline = route.query.view === 'months';
  const selected = Math.min(Math.max(asked || model.nowMonth || 1, 1), model.count);
  const row = model.byNumber.get(selected);
  const addBatch = () => openAddBatch(timeline
    ? { clientId: client.id }
    : { clientId: client.id, monthId: row.month.id, monthNumber: selected });
  const toggleView = () => navigate(clientHref(client.id, asked, { view: timeline ? '' : 'months' }).slice(1), { replace: true });

  return (
    <Page
      class="cl-page"
      header={(
        <div class="cl-head">
          <header class="page-header">
            <div class="page-heading cl-title-row">
              <h1 class="page-title" title={client.name}>{client.name}</h1>
              <Button variant="ghost" size="sm" icon="edit" class="cl-edit" aria-label={`Edit ${client.name}`} onClick={() => openEditClient(client)}>Edit</Button>
            </div>
            <div class="page-actions">
              <button type="button" class={cx('btn btn-ghost cl-months-toggle', timeline && 'is-on')} aria-pressed={timeline ? 'true' : 'false'} onClick={toggleView}>
                <Icon name="calendar" size={15} />All months
              </button>
              <Button variant="primary" icon="plus" onClick={addBatch}>Add Batch</Button>
            </div>
          </header>
          <ClientLine client={client} state={state} index={index} today={today} />
        </div>
      )}
    >
      {timeline ? (
        <Timeline client={client} months={model.months} selected={selected} nowMonth={model.nowMonth} />
      ) : (
        <>
          <MonthSelector
            months={model.months.map((m) => ({ monthNumber: m.monthNumber, status: m.status, tag: batchTag(m.batches), tagTitle: batchNames(m.batches) || 'no batch' }))}
            selected={selected}
            nowMonth={model.nowMonth}
            onSelect={(n) => goToMonth(client.id, n)}
            label={`${client.name} months`}
            idPrefix="cl-month"
            controls="cl-month-panel"
          />
          <section class="cl-month" id="cl-month-panel" role="tabpanel" aria-labelledby={`cl-month-tab-${selected}`} tabIndex={-1}>
            <MonthLine key={selected} row={row} isNow={selected === model.nowMonth} client={client} months={model.months} />
            <BatchTable
              batches={row.batches}
              index={index}
              today={today}
              showClient={false}
              showMonth={false}
              label={`${monthLabel(selected)} batches`}
              class="cl-table"
              empty={row.off
                ? <EmptyState compact title="This month is off." />
                : <EmptyState compact title="No batch in this month yet." action={<Button size="sm" icon="plus" onClick={addBatch}>Add Batch</Button>} />}
            />
          </section>
        </>
      )}
    </Page>
  );
}

// ---------- parts ----------

const safeUrl = (u) => (/^https?:\/\//i.test(String(u || '').trim()) ? String(u).trim() : '');

// One compact line under the client name: status · contract progress · owner + PICs · handles · posting days · start ·
// contact · links (DESIGN §9).
function ClientLine({ client, state, index, today }) {
  const owner = client.owner ? index.teamById.get(client.owner) : null;
  const contact = [client.contactName, client.email, client.phone].filter(Boolean);
  const contract = clientContract(state, index, client, today);
  const pic = picOf(client);
  const pics = PIC_LABELS.map(([key, label]) => ({ label, person: index.teamById.get(pic[key]) })).filter((p) => p.person);
  const posts = postingDaysText(postingDaysOf(client));
  const ig = instagramUrl(client.instagram);
  const tt = tiktokUrl(client.tiktok);
  return (
    <div class="cl-line">
      <StatusBadge status={client.status} kind="client" />
      {contract && <span class="cl-fact cl-contract num" title="Contract progress">{contract.text}</span>}
      <span class="cl-fact" title="Account owner">
        <Avatar name={owner?.name} size={18} />
        <span class="truncate">{owner ? owner.name : 'Unassigned'}</span>
        {pics.length > 0 && (
          <span class="cl-pics" role="img" aria-label={pics.map((p) => `${p.label}: ${p.person.name}`).join(', ')}>
            {pics.map((p) => <Avatar key={p.label} name={p.person.name} size={18} title={`${p.label}: ${p.person.name}`} />)}
          </span>
        )}
      </span>
      {ig && <a class="cl-fact link-quiet cl-handle" href={ig} target="_blank" rel="noopener noreferrer" title="Instagram"><span class="cl-net">Instagram</span>@{client.instagram}</a>}
      {tt && <a class="cl-fact link-quiet cl-handle" href={tt} target="_blank" rel="noopener noreferrer" title="TikTok"><span class="cl-net">TikTok</span>@{client.tiktok}</a>}
      {posts && <span class="cl-fact cl-posts" title="Posting days">Posts {posts}</span>}
      {client.startDate && <span class="cl-fact cl-start num">Started {formatDate(client.startDate, 'long')}</span>}
      {contact.length > 0 && (
        <span class="cl-fact cl-contact" title="Contact">
          {client.contactName && <span class="truncate">{client.contactName}</span>}
          {client.email && <span class="truncate">{client.email}</span>}
          {client.phone && <span class="num">{client.phone}</span>}
        </span>
      )}
      {safeUrl(client.folderUrl) && <ExternalLink class="link-quiet cl-fact" href={safeUrl(client.folderUrl)}>Folder</ExternalLink>}
      {safeUrl(client.website) && <ExternalLink class="link-quiet cl-fact" href={safeUrl(client.website)}>Website</ExternalLink>}
    </div>
  );
}

const PIC_LABELS = [['script', 'Script PIC'], ['videographer', 'Videographer PIC'], ['editor', 'Editor PIC']];

const SAVE_TEXT = { saving: 'Saving…', saved: 'Saved', error: 'Not saved' };
const SIZES_ITSELF = typeof CSS !== 'undefined' && !!CSS.supports?.('field-sizing', 'content');

// One click, no modal: Off turns the month red (and back). The shared layer logs the activity. One toggle element
// (aria-pressed) that is never disabled, so keyboard focus stays on it after Enter.
function OffToggle({ month, off }) {
  const [busy, setBusy] = useState(false);
  const run = () => {
    if (busy) return;
    setBusy(true);
    dispatch('month.setOff', { ...monthRef(month), off: !off })
      .then(() => toast(off ? `${monthLabel(month.monthNumber)} resumed.` : `${monthLabel(month.monthNumber)} marked off.`))
      .catch(() => {})
      .finally(() => setBusy(false));
  };
  return (
    <Button size="sm" variant={off ? 'secondary' : 'ghost'} icon={off ? 'play' : undefined} class="cl-off-btn"
      onClick={run} aria-pressed={off ? 'true' : 'false'} aria-busy={busy ? 'true' : undefined}>
      {off ? 'Resume' : 'Mark month off'}
    </Button>
  );
}

// An off month that still holds open work: one click moves that batch to the next working month (Batch 6 → Month 9).
// The deadline moves with the batch when it was the old month's end (the Add Batch default), empty, or before the
// target month — otherwise the moved batch would be overdue at once.
function MoveBatch({ batch, client, target, from, months }) {
  const [busy, setBusy] = useState(false);
  const run = () => {
    if (busy) return;
    setBusy(true);
    const to = months.find((m) => m.monthNumber === target) || monthRange(client.startDate, target);
    const due = batch.dueDate || '';
    const patch = { monthNumber: target };
    if (to.endDate && (!due || due === from.endDate || due < to.startDate)) patch.dueDate = to.endDate;
    dispatch('batch.update', { id: batch.id, patch })
      .then(() => {
        toast(`${batchLabel(batch.batchNumber)} moved to ${monthLabel(target)}.`, { tone: 'success' });
        goToMonth(client.id, target);
      })
      .catch(() => setBusy(false));
  };
  return (
    <Button size="sm" variant="secondary" icon="arrowRight" class="cl-move-btn" onClick={run} aria-busy={busy ? 'true' : undefined}>
      Move {batchLabel(batch.batchNumber)} to {monthLabel(target)}
    </Button>
  );
}

// The first month after n that is not off and holds no batch (created on demand, capped at 99).
function nextWorkingMonth(months, n) {
  for (let k = n + 1; k <= MAX_MONTH; k++) {
    const r = months.find((m) => m.monthNumber === k);
    if (!r || (!r.off && !r.batches?.length)) return k;
  }
  return 0;
}

// MONTH 09 · Sep 21 – Oct 20, 2026 · Batch 06 · [Mark month off] + month notes (saved while typing).
function MonthLine({ row, isNow, client, months }) {
  const { month } = row;
  const open = row.off ? (row.batches || []).find((b) => b.status !== 'Done') : null;
  const target = open ? nextWorkingMonth(months, row.monthNumber) : 0;
  return (
    <div class={cx('cl-month-line', row.off && 'is-off')}>
      <div class="cl-month-facts">
        <span class="eyebrow cl-month-name">{monthLabel(row.monthNumber)}</span>
        <span class="num">{formatDateRange(row.startDate, row.endDate)}</span>
        <span class="cl-sep" aria-hidden="true">·</span>
        <span class="num">{batchNames(row.batches) || 'No batch'}</span>
        {isNow && <span class="cl-now">Current month</span>}
        {row.off && <span class="cl-off-text">Month is off</span>}
        <OffToggle month={month} off={row.off} />
        {open && target > 0 && <MoveBatch batch={open} client={client} target={target} from={row} months={months} />}
      </div>
      <MonthNotes month={month} />
    </div>
  );
}

function MonthNotes({ month }) {
  const notes = useAutosave(month.notes, (text, base) => saveMonthNotes(month, text, base), {
    onUnload: (text, base) => sendOnUnload('month.update', { ...monthRef(month), patch: { notes: text.trim(), notesBase: base } }),
  });
  const inputRef = useRef(null);
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el || SIZES_ITSELF) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [notes.value]);
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); notes.flush(); }
  };
  return (
    <label class="cl-notes">
      <span class="sr-only">{monthLabel(month.monthNumber)} notes</span>
      <textarea class="cl-notes-input" rows={1} maxLength={5000} placeholder="Add month notes…" ref={inputRef} onKeyDown={onKeyDown} {...notes.bind} />
      <span class={cx('cl-notes-status meta-xs', notes.status === 'error' && 'text-danger')} role="status" aria-live="polite">
        {SAVE_TEXT[notes.status] || ''}
      </span>
    </label>
  );
}

// "All months": Month | Dates | Batch | Progress | Status — Off rows red. A row opens that month (and leaves the timeline).
function Timeline({ client, months, selected, nowMonth }) {
  return (
    <div class="table cl-timeline" role="group" aria-label={`${client.name} timeline`}>
      <div class="table-row table-head cl-tl-row" aria-hidden="true">
        <span>Month</span>
        <span>Dates</span>
        <span>Batch</span>
        <span>Progress</span>
        <span>Status</span>
      </div>
      {months.map((m) => {
        const n = m.monthNumber;
        const first = m.batches[0];
        return (
          <a
            key={n}
            class={cx('table-row cl-tl-row', n === selected && 'is-selected', m.off && 'is-off')}
            href={clientHref(client.id, n)}
            aria-current={n === selected ? 'true' : undefined}
          >
            <span class="cl-tl-month">
              {monthLabel(n)}
              {n === nowMonth && <span class="cl-tl-now" title="Current month"><span class="sr-only">, current month</span></span>}
            </span>
            <span class="num cell-muted truncate cl-tl-dates">{formatDateRange(m.startDate, m.endDate)}</span>
            <span class="truncate cl-tl-batch">{batchNames(m.batches) || <span class="faint">—</span>}</span>
            <span class="cl-tl-progress">{first ? <><StageProgress batch={first} variant="mini" /><span class="truncate">{stageProgressText(first)}</span></> : <span class="faint">—</span>}</span>
            <span class="cl-tl-status"><StatusBadge status={m.status} kind="month" tone={m.off ? 'danger' : undefined} /></span>
          </a>
        );
      })}
    </div>
  );
}
